import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@shared/api/client';
import { acceptScopeForm, getScopeForm } from '@shared/api/scope-form';
import { useMe } from '@shared/hooks/use-me';
import type { ChatMessage, Uuid } from '@shared/types/api';

type Props = {
  message: ChatMessage;
  transactionId: Uuid;
  isAdminViewer?: boolean;
  onOpenAdminReview?: () => void;
};

export function ScopeFormCard({
  message,
  transactionId,
  isAdminViewer = false,
  onOpenAdminReview,
}: Props) {
  const qc = useQueryClient();
  const meta = message.metadata || {};

  const { data: me } = useMe();

  const { data: liveScopeForm } = useQuery({
    queryKey: ['scope-form', transactionId],
    queryFn: () => getScopeForm(transactionId),
    staleTime: 10_000,
  });

  const isAssociateViewer =
    !!liveScopeForm?.createdByAssociateId && me?.id === liveScopeForm.createdByAssociateId;
  const isClientViewer = !isAdminViewer && !isAssociateViewer;

  const status = liveScopeForm?.status ?? (meta.status as string) ?? 'PROPOSED';
  const isProposed = status === 'PROPOSED';
  const isFinalized = status === 'FINALIZED';
  const isAccepted = status === 'ACCEPTED';

  const acceptMutation = useMutation({
    mutationFn: () => acceptScopeForm(transactionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-form', transactionId] });
      qc.invalidateQueries({ queryKey: ['transaction', transactionId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['chat', transactionId, 'history'] });
    },
  });

  const isProposedCard = meta.kind === 'scope_form:proposed';

  const profFee = isProposedCard
    ? ((meta.professionalFeeRupees as string) ?? liveScopeForm?.professionalFeeRupees ?? '0')
    : (liveScopeForm?.professionalFeeRupees ?? (meta.professionalFeeRupees as string) ?? '0');

  const govtFee = isProposedCard
    ? ((meta.governmentFeeRupees as string) ?? liveScopeForm?.governmentFeeRupees ?? '0')
    : (liveScopeForm?.governmentFeeRupees ?? (meta.governmentFeeRupees as string) ?? '0');

  // Hide platform fee from associate proposal cards and fulfilling associate viewers
  const platFee =
    isProposedCard || isAssociateViewer
      ? null
      : (liveScopeForm?.platformFeeRupees ?? (meta.platformFeeRupees as string | null) ?? null);

  const totalRupees =
    isProposedCard || isAssociateViewer
      ? (Number(profFee) + Number(govtFee)).toFixed(2)
      : (liveScopeForm?.totalRupees ??
        (meta.totalRupees as string) ??
        (Number(profFee) + (platFee ? Number(platFee) : 0) + Number(govtFee)).toFixed(2));

  return (
    <div className="flex justify-center my-2">
      <div className="max-w-lg w-full rounded-xl border border-brand-200 bg-white p-4 shadow-sm text-left">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-xs text-brand-900 uppercase tracking-wider">
              {meta.kind === 'scope_form:proposed'
                ? 'Scope & Fee Proposal'
                : isFinalized || isAccepted
                  ? 'Final Quotation Form'
                  : 'Scope & Fee Proposal'}
            </span>
          </div>
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
              meta.kind === 'scope_form:proposed'
                ? 'bg-amber-100 text-amber-800'
                : isAccepted
                  ? 'bg-emerald-100 text-emerald-800'
                  : isFinalized
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-amber-100 text-amber-800'
            }`}
          >
            {meta.kind === 'scope_form:proposed'
              ? 'Associate Proposal'
              : isAccepted
                ? 'Accepted'
                : isFinalized
                  ? 'Quotation Ready'
                  : 'Pending Admin Review'}
          </span>
        </div>

        <div className="space-y-3 text-xs text-gray-800">
          {meta.serviceDescription ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Service Description
              </span>
              <p className="mt-0.5 whitespace-pre-wrap">{String(meta.serviceDescription)}</p>
            </div>
          ) : null}

          {meta.workScope ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Work Scope
              </span>
              <p className="mt-0.5 whitespace-pre-wrap">{String(meta.workScope)}</p>
            </div>
          ) : null}

          {meta.deliverables ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Deliverables
              </span>
              <p className="mt-0.5 whitespace-pre-wrap">{String(meta.deliverables)}</p>
            </div>
          ) : null}

          <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3 space-y-1.5">
            <span className="font-semibold text-gray-600 block text-[10px] uppercase mb-1">
              Fee &amp; Charges Breakdown
            </span>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-600">Professional Fee:</span>
              <span className="font-mono font-medium">₹{profFee}</span>
            </div>

            {Number(govtFee) > 0 ? (
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">Government / Statutory Fee:</span>
                <span className="font-mono font-medium">₹{govtFee}</span>
              </div>
            ) : null}

            {/* Platform fee is hidden from associate/franchise viewer */}
            {platFee !== null && platFee !== undefined ? (
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">Platform Fee:</span>
                <span className="font-mono font-medium text-brand-700">₹{platFee}</span>
              </div>
            ) : null}

            <div className="border-t border-gray-200 pt-1.5 flex justify-between items-center text-sm font-bold text-gray-900">
              <span>Total Quotation Amount:</span>
              <span className="font-mono text-brand-800">₹{totalRupees}</span>
            </div>
          </div>

          {meta.expectedTat ? (
            <div className="flex justify-between items-center text-xs text-gray-700">
              <span className="text-gray-500 font-medium">Expected TAT:</span>
              <span className="font-semibold">{String(meta.expectedTat)}</span>
            </div>
          ) : null}

          {meta.additionalRequirements ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Additional Requirements
              </span>
              <p className="mt-0.5 text-gray-700">{String(meta.additionalRequirements)}</p>
            </div>
          ) : null}

          {meta.exclusions ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Exclusions
              </span>
              <p className="mt-0.5 text-gray-700">{String(meta.exclusions)}</p>
            </div>
          ) : null}

          {meta.specialConditions ? (
            <div>
              <span className="font-semibold text-gray-500 block text-[10px] uppercase">
                Special Conditions
              </span>
              <p className="mt-0.5 text-gray-700">{String(meta.specialConditions)}</p>
            </div>
          ) : null}
        </div>

        {/* Action button cluster */}
        <div className="mt-4 pt-2 border-t border-gray-100 flex flex-col items-center gap-2">
          {/* CLIENT ONLY: Accept button — only when FINALIZED and not yet accepted */}
          {isFinalized && isClientViewer ? (
            <Button
              className="w-full bg-brand-700 hover:bg-brand-800 text-white font-medium text-xs py-2 shadow-xs"
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending}
            >
              {acceptMutation.isPending ? 'Accepting…' : '✓ Accept Final Quotation'}
            </Button>
          ) : null}

          {/* ALL VIEWERS: Show accepted confirmation on final quotation card once client has accepted */}
          {isAccepted && meta.kind !== 'scope_form:proposed' ? (
            <div className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
              <svg
                className="w-4 h-4 text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Quotation Accepted by Client. Awaiting Invoice from Admin.
            </div>
          ) : null}

          {/* ADMIN ONLY: Review button — only when PROPOSED (before admin finalizes) */}
          {isProposed && isAdminViewer && onOpenAdminReview ? (
            <Button
              variant="outline"
              className="w-full border-brand-600 text-brand-800 hover:bg-brand-50 text-xs py-1.5 font-medium"
              onClick={onOpenAdminReview}
            >
              Review &amp; Enter Platform Fee
            </Button>
          ) : null}

          {/* ADMIN ONLY: View-only notice after finalized or accepted on final quotation card */}
          {(isFinalized || isAccepted) && isAdminViewer && !isProposedCard ? (
            <div className="text-[10px] text-gray-400 text-center">
              {isAccepted
                ? 'Client has accepted this quotation. Proceed to create invoice.'
                : 'Final quotation sent to client. Awaiting their acceptance.'}
            </div>
          ) : null}

          {acceptMutation.isError ? (
            <p className="mt-2 text-xs text-red-600">{apiErrorMessage(acceptMutation.error)}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
